import jsCookie from 'js-cookie';
import { USER_STATES, ANALYTICS_ACCOUNT_PATH } from './constants';

const intitialVaues = {
  geo_country: null,
  geo_state: null,
  keep_me_logged_in_flag: null,
  loyalty_join_date: null,
  loyalty_points: null,
  loyalty_to_next_reward: null,
  loyalty_vip_status: null,
  product_recommendation_placement: null,
  product_recommendation_product_id: null,
  rcs_id: null,
  recommendations: null,
  user_hashed_email: null,
  user_state: null,
};
/**
 * Safely merges data into window.digitalData
 * @param {Object} data - The data to merge
 */
const mergeAnalyticsData = (data) => {
  if (!data) return;
  window?.digitalData?.merge(ANALYTICS_ACCOUNT_PATH, data);
};

const STORAGE_KEY_KEEP_ME_LOGGED_IN = 'keep_me_logged_in_flag';
const GEOLOCATION_COOKIE = 'geoLocation';

/**
 * @name getGeoDataFromCookie
 * @description Retrieves geolocation data from the 'geoLocation' cookie. Supports both colon-separated (e.g., "US:CA") and URL parameter (e.g., "country=US&region=CA") formats.
 * @public
 * @returns {{country?: string, state?: string}} An object containing country and state if available.
 */
function getGeoDataFromCookie() {
  // Get the geoLocation cookie using js-cookie
  const value = jsCookie.get(GEOLOCATION_COOKIE);

  if (!value) {
    // Cookie not found, return empty object
    return {};
  }

  try {
    const decodedValue = decodeURIComponent(value);

    // If value contains a colon, treat as colon-separated format (e.g., "US:CA")
    if (decodedValue.includes(':')) {
      const [country, state] = decodedValue.split(':');
      return {
        country: country || undefined,
        state: state || undefined,
      };
    }

    // Otherwise, treat as URL parameter format (e.g., "country=US&region=CA")
    const parts = decodedValue.split('&').reduce((acc, part) => {
      const [key, val] = part.split('=');
      acc[key] = val;
      return acc;
    }, {});
    return {
      country: parts.country,
      state: parts.region,
    };
  } catch {
    // If parsing fails, return empty object
    return {};
  }
}

/**
 * @name getGeoData
 * @description Retrieves geolocation data using OneTrust if available, otherwise falls back to cookie-based geolocation.
 * @public
 * @returns {{country?: string, state?: string}} An object containing country and state if available.
 */
function getGeoData() {
  let geoData = {};

  // Prefer OneTrust geolocation if available
  if (window?.OneTrust?.getGeolocationData) {
    geoData = window.OneTrust.getGeolocationData() || {};
  }

  // If country or state is missing, supplement with cookie data
  if (!geoData.country || !geoData.state) {
    const cookieGeo = getGeoDataFromCookie();
    geoData = {
      ...geoData,
      ...cookieGeo,
    };
  }

  return geoData;
}

/**
 * Sets user authentication data in analytics based on user state
 * Handles both authenticated and guest user scenarios
 */
export const analytics = {
  user: {
    setData: ({
      id,
      hashedEmail,
    }) => {
      const isUserAuthenticated = Boolean(id);
      // Define user data based on authentication status
      const userData = isUserAuthenticated
        ? {
          user_hashed_email: hashedEmail || null,
          user_state: USER_STATES.AUTHENTICATED,
        }
        : {
          user_state: USER_STATES.GUEST,
        };

      // Merge user data into analytics
      mergeAnalyticsData({ ...intitialVaues, ...userData });
      // Fall back to support old version of digital data
      if (isUserAuthenticated) {
        const oldHashedEmail = window?.digitalData?.export?.()?.user?.hashedEmail;
        if (!oldHashedEmail) {
          window?.digitalData?.merge('user', {
            hashedEmail,
          });
        }
      }
    },

    setKeepMeLoggedIn: (isKeepMeLoggedIn) => {
      mergeAnalyticsData({
        keep_me_logged_in_flag: Boolean(isKeepMeLoggedIn),
      });
      // fallback to support old version of digital data
      const keepMeLoggedInFlag = sessionStorage.getItem(STORAGE_KEY_KEEP_ME_LOGGED_IN);
      if (!keepMeLoggedInFlag) {
        sessionStorage.setItem(STORAGE_KEY_KEEP_ME_LOGGED_IN, isKeepMeLoggedIn);
      }
    },
  },

  /**
   * Loyalty program analytics methods
   * Handles tracking of VIP status, points, and rewards progress
   */
  loyalty: {
    setTier: (tierImageSrc) => {
      if (!tierImageSrc) return;

      const vipStatus = tierImageSrc.includes('vip') ? 'VIP' : 'base';
      const geoData = getGeoData();
      mergeAnalyticsData({
        loyalty_vip_status: vipStatus,
        geo_state: geoData?.state,
        geo_country: geoData?.country,
        keep_me_logged_in_flag: sessionStorage.getItem(STORAGE_KEY_KEEP_ME_LOGGED_IN),
      });

      // Fallback to support old version of digital data
      const oldTier = window?.digitalData?.get?.('user.loyalty.tier');
      if (!oldTier) {
        window?.digitalData?.set?.('user.loyalty.tier', vipStatus);
      }
    },

    setPoints: (points) => {
      const pointsValue = Number(points);
      if (Number.isNaN(pointsValue)) return;

      mergeAnalyticsData({
        loyalty_points: pointsValue,
      });

      // Fallback to support old version of digital data
      const oldPoints = window?.digitalData?.get?.('user.loyalty.points');
      if (!oldPoints) {
        window?.digitalData?.set?.('user.loyalty.points', pointsValue);
      }
    },

    setPointsToNextReward: (points) => {
      const pointsValue = Number(points);
      if (Number.isNaN(pointsValue)) return;

      mergeAnalyticsData({
        loyalty_to_next_reward: pointsValue,
      });

      // Fallback to support old version of digital data
      const oldNextReward = window?.digitalData?.get?.('user.loyalty.nextReward');
      if (!oldNextReward) {
        window?.digitalData?.set?.('user.loyalty.nextReward', pointsValue);
      }
    },
  },
};
